import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Icon } from '@/icons/Icon'
import { Loader } from '@/components/Loader'

import { message, pGet, pPost, type OrgRow, type Tenancy } from './api'
import { generateHandoverPassword, hashFor } from './handover'
import { HandoverPassword } from './HandoverPassword'

/**
 * Every carrier on this installation.
 *
 * Counts and configuration, never content: how many people and how many
 * circulars a carrier has, not what any of them say. One organization not
 * seeing another's data is the product's central promise, and it would be a
 * strange promise to keep from the carriers and break for the platform.
 */

interface Listing {
  organizations: OrgRow[]
  total: number
  tenancy: Tenancy
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function domainOf(email: string): string {
  const at = email.indexOf('@')
  return at > 0 ? email.slice(at + 1).trim().toLowerCase() : ''
}

type StatusFilter = '' | 'active' | 'inactive'

export default function OrganizationsPage() {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<StatusFilter>('')
  const [creating, setCreating] = useState(false)

  // The search is the server's - it knows the names, domains and subdomains.
  // The status is not: filtering it there made the tiles above contradict
  // themselves, because "Switched off · 0" was then a statement about the
  // filter rather than about the installation. The list is one row per carrier
  // on one installation, so the three counts are free to compute here.
  const listing = useQuery({
    queryKey: ['pf', 'orgs', q],
    queryFn: () => pGet<Listing>('/organizations', { q: q.trim() || undefined }),
  })

  const all = listing.data?.organizations ?? []
  const tenancy = listing.data?.tenancy

  const live = useMemo(() => all.filter((o) => o.isActive).length, [all])
  const rows = useMemo(
    () => (status === '' ? all : all.filter((o) => o.isActive === (status === 'active'))),
    [all, status],
  )

  const metrics = useMemo(
    () =>
      [
        { key: 'all', label: 'Carriers', value: all.length, foot: 'On this installation', accent: 'blue', tile: 'brand', icon: 'layout-grid' },
        { key: 'live', label: 'Live', value: live, foot: 'Signing in', accent: 'green', tile: 'success', icon: 'check' },
        { key: 'off', label: 'Switched off', value: all.length - live, foot: 'Sign-in refused', accent: 'amber', tile: 'warning', icon: 'alert-triangle' },
        { key: 'people', label: 'People', value: all.reduce((n, o) => n + o.activeUsers, 0), foot: 'Active accounts', accent: 'violet', tile: 'info', icon: 'user' },
      ] as const,
    [all, live],
  )

  const filters = [
    { key: '', label: 'All', n: all.length },
    { key: 'active', label: 'Live', n: live },
    { key: 'inactive', label: 'Off', n: all.length - live },
  ] as const

  return (
    <>
      <div className="pf-head">
        <div>
          <h1 className="pf-head__h">Organizations</h1>
          <p className="pf-head__d">Every carrier on this installation.</p>
        </div>
        <div className="pf-head__acts">
          <button className="ix-btn ix-btn--compact" type="button" onClick={() => setCreating((v) => !v)}>
            <Icon name={creating ? 'chevron-right' : 'plus'} size={14} />
            {creating ? 'Cancel' : 'New organization'}
          </button>
        </div>
      </div>

      <section className="ix-grid ix-grid--4 pf-rise" style={{ marginBottom: 18 }}>
        {metrics.map((m) => (
          <article className={`ix-metric ix-accent-${m.accent}`} key={m.key}>
            <div className="ix-metric__top">
              <div className="ix-metric__labels">
                <div className="ix-metric__label">{m.label}</div>
              </div>
              <span className={`ix-icon-tile ix-icon-tile--${m.tile} ix-accent-tile`}>
                <Icon name={m.icon} size={16} />
              </span>
            </div>
            <div className="ix-metric__value-row">
              <div className="ix-metric__value ix-accent-value">{listing.isLoading ? '—' : m.value}</div>
            </div>
            <div className="ix-metric__foot">{m.foot}</div>
          </article>
        ))}
      </section>

      {tenancy && !tenancy.enabled ? (
        <div className="ix-alert ix-alert--warning" role="status" style={{ marginBottom: 14 }}>
          <div>
            Subdomains are recorded but not live: <code>TENANT_BASE_DOMAIN</code> is unset.
          </div>
        </div>
      ) : null}

      {creating ? (
        <CreateOrganization
          onDone={() => {
            setCreating(false)
            void qc.invalidateQueries({ queryKey: ['pf', 'orgs'] })
          }}
        />
      ) : null}

      {listing.isError ? (
        <div className="ix-alert ix-alert--error" role="alert" style={{ marginBottom: 14 }}>
          <div>{message(listing.error, 'The organizations could not be loaded.')}</div>
        </div>
      ) : null}

      <div className="pf-card">
        <div className="pf-toolbar">
          <label className="st-search">
            <Icon name="search" size={15} />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, domain or subdomain"
              aria-label="Search organizations"
            />
          </label>
          {/* Three mutually exclusive choices, so every one is readable
              without opening anything, and the count beside each says what
              picking it costs. */}
          <div className="pf-seg" role="group" aria-label="Filter by status">
            {filters.map((f) => (
              <button
                key={f.key || 'all'}
                className={status === f.key ? 'pf-seg__b is-on' : 'pf-seg__b'}
                type="button"
                aria-pressed={status === f.key}
                onClick={() => setStatus(f.key)}
              >
                {f.label}
                <span className="pf-seg__n">{f.n}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="pf-card__scroll">
          <table className="ix-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Mail domain</th>
                <th>Subdomain</th>
                <th className="pf-num">People</th>
                <th className="pf-num">Circulars</th>
                <th>Created</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {listing.isLoading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="pf-loading">
                      <Loader size="md" label="Loading organizations" />
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="pf-empty">
                    <span className="pf-empty__t">
                      {q || status ? 'Nothing matches that' : 'No carriers yet'}
                    </span>
                    {q || status
                      ? 'Clear the search or the filter to see the rest.'
                      : 'New organization creates one, with its first administrator.'}
                  </td>
                </tr>
              ) : (
                rows.map((org) => (
                  <tr key={org.id}>
                    <td>
                      <div className="st-user">
                        <span className="ix-avatar ix-avatar--sm">
                          {org.name.slice(0, 2).toUpperCase()}
                        </span>
                        <span className="st-user__id">
                          <Link className="st-user__n" to={`/admin/organizations/${org.id}`}>
                            {org.name}
                          </Link>
                          <span className="st-user__e">
                            {org.onboardingCompletedAt ? 'Onboarded' : 'Onboarding not finished'}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td>{org.domain || <span className="st-muted">—</span>}</td>
                    <td>{org.subdomain || <span className="st-muted">—</span>}</td>
                    <td className="pf-num">{org.activeUsers}</td>
                    <td className="pf-num">{org.circulars}</td>
                    <td className="pf-nowrap">{fmtDate(org.createdAt)}</td>
                    <td>
                      {org.isActive ? (
                        <span className="ix-chip ix-chip--success">Live</span>
                      ) : (
                        <span className="ix-chip ix-chip--error">Off</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

/**
 * One form: the carrier and its first administrator.
 *
 * They are the same fact. `organizations.domain` decides who may be invited
 * and who may sign in, and the first account is by definition somebody at the
 * carrier - so the domain comes from their address unless it is overridden,
 * and the API refuses a pair where the administrator could not sign in to the
 * organization being created.
 */
function CreateOrganization({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [domain, setDomain] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminName, setAdminName] = useState('')
  const [created, setCreated] = useState<{ org: OrgRow; email: string; password: string } | null>(null)

  const derived = useMemo(() => domainOf(adminEmail), [adminEmail])

  const create = useMutation({
    mutationFn: async () => {
      const email = adminEmail.trim().toLowerCase()
      const password = generateHandoverPassword()
      const org = await pPost<OrgRow>('/organizations', {
        name: name.trim(),
        domain: domain.trim() || null,
        subdomain: subdomain.trim() || null,
        adminEmail: email,
        adminFullName: adminName.trim(),
        adminPassword: await hashFor(password, email),
      })
      return { org, email, password }
    },
    onSuccess: setCreated,
  })

  if (created) {
    return (
      <section className="st-panel pf-sec" style={{ marginBottom: 18 }}>
        <header className="st-panel__head">
          <div>
            <div className="st-panel__t">{created.org.name} created</div>
            <div className="st-panel__d">
              Domain {created.org.domain} · owner {created.email}
            </div>
          </div>
          <span className="ix-chip ix-chip--success">Live</span>
        </header>
        <div className="st-panel__body">
          <HandoverPassword who={created.email} password={created.password} />
          <div className="st-form__actions">
            <button className="ix-btn ix-btn--primary" type="button" onClick={onDone}>
              Done
            </button>
          </div>
        </div>
      </section>
    )
  }

  const ready = name.trim() && adminEmail.trim() && adminName.trim()

  return (
    <section className="st-panel pf-sec" style={{ marginBottom: 18 }}>
      <header className="st-panel__head">
        <div>
          <div className="st-panel__t">New organization</div>
          <div className="st-panel__d">The carrier and its first administrator.</div>
        </div>
      </header>
      <form
        className="st-panel__body"
        onSubmit={(e) => {
          e.preventDefault()
          if (create.isPending || !ready) return
          create.mutate()
        }}
      >
        <div className="st-grid">
          <label className="st-field">
            <span>Carrier name</span>
            <input
              className="ix-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Northwind Mutual"
              required
            />
          </label>

          <label className="st-field">
            <span>Subdomain</span>
            <input
              className="ix-input"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              placeholder="northwind"
              autoCapitalize="off"
              spellCheck={false}
            />
          </label>

          <label className="st-field">
            <span>First administrator</span>
            <input
              className="ix-input"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="ada@northwind-mutual.com"
              required
            />
          </label>

          <label className="st-field">
            <span>Their name</span>
            <input
              className="ix-input"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              placeholder="Ada Reeve"
              required
            />
          </label>

          <label className="st-field st-field--wide">
            <span>Mail domain</span>
            <input
              className="ix-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder={derived || 'northwind-mutual.com'}
              autoCapitalize="off"
              spellCheck={false}
            />
            <small>{derived && !domain.trim() ? `Blank uses ${derived}.` : 'Comma-separated.'}</small>
          </label>
        </div>

        {create.error ? (
          <div className="ix-alert ix-alert--error" role="alert" style={{ marginTop: 14 }}>
            <div>{message(create.error, 'The organization could not be created.')}</div>
          </div>
        ) : null}

        <div className="st-form__actions">
          <button className="ix-btn ix-btn--primary" type="submit" disabled={create.isPending || !ready}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
          <button className="ix-btn ix-btn--secondary" type="button" onClick={onDone}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  )
}
